import React, { RefObject, useCallback, useContext, useState } from "react";

import { createPortal } from "react-dom";

import { LinkHandler } from "@/components/common/LinkHandler";
import SubscriptionIncludesModal from "@/components/common/SubscriptionIncludes/SubscriptionIncludesModal";
import { ModalNova } from "@/components/ui-kit/ModalNova/ModalNova";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { DUO_MEN_PAGES, DUO_PAGES, PDP_MEN_PAGES, PDP_PAGES, RIBBON_MAP } from "@/constants";
import UserContext, { UserContextType } from "@/context/UserContext";
import { data } from "@/data/subscription/data_headspace";
import { useNavigation } from "@/hooks/useNavigation";
import { HeaderRibbonFields } from "@/types/common";
import { renderRichTextDinamo } from "@/utils/contentful";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";
import { ThemeTypes } from "@/utils/theming";

import styles from "./Ribbon.module.scss";

interface RibbonProps {
  PROMO_VARIANT?: keyof typeof RIBBON_MAP;
  contentfulHeaderRibbon?: HeaderRibbonFields | null;
  pathname?: string;
  isMobile?: boolean;
  isMouseInHeader?: RefObject<boolean>;
  theme?: ThemeTypes;
}

const Ribbon: React.FC<RibbonProps> = ({
  PROMO_VARIANT = "mayGwp",
  contentfulHeaderRibbon,
  pathname = "",
  isMobile = false,
}) => {
  /*
   * test blue ribbon
   * PROMO_VARIANT = "pick_your_booster";
   */

  const [isClosed] = useState(false);
  const userState = useContext<UserContextType | null>(UserContext);
  const className = "rgg-promo";
  const [modalOpen, setmodalOpen] = useState(false);
  const navigate = useNavigation();
  const openModal = useCallback(() => {
    setmodalOpen(true);
  }, []);

  const hideModal = useCallback(() => {
    setmodalOpen(false);
  }, []);

  if (!contentfulHeaderRibbon && !PROMO_VARIANT) {
    return null;
  }

  let ribbonHtml: string = renderRichTextDinamo(contentfulHeaderRibbon?.ribbonText);
  let isPromo: boolean | undefined = contentfulHeaderRibbon?.ispromo;
  let ribbonHref: string | undefined = contentfulHeaderRibbon?.ribbonLink;
  let isModal = [...PDP_MEN_PAGES, ...DUO_MEN_PAGES, ...PDP_PAGES, ...DUO_PAGES].includes(pathname);

  //Ribbon target logic
  if ([ribbonHref].includes(pathname)) {
    /*
     * ribbonHtml = `<span class="font-bold">Real Growth Guarantee</span>—Results in 6 months or your money back.<sup>∞</sup>`;
     * ribbonHref = undefined;
     */
    isModal = true;
  }

  if (RIBBON_MAP[PROMO_VARIANT]?.copy && PROMO_VARIANT !== "ribbon_products") {
    ribbonHtml = RIBBON_MAP[PROMO_VARIANT].copy.replace(
      "{name}",
      userState?.customer?.firstname?.trimEnd() || ""
    );
    isPromo = false;
    isModal = false;
    ribbonHref = RIBBON_MAP[PROMO_VARIANT].href;
  }

  if (pathname === ribbonHref) {
    ribbonHref = null;
  }

  const isRgg = ribbonHref?.includes("real-growth-guarantee");

  const getRibbonAttrs = () => ({
    destination: ribbonHref,
    text: typeof ribbonHtml === "string" ? ribbonHtml.replace(/(<([^>]+)>)/gi, "") : "",
    type: "ribbon",
    module_location: "ribbon",
  });

  const handleRibbonClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    GA4.onMarketingCtaClicked(getRibbonAttrs());
    e.preventDefault();
    if (isRgg) {
      if (isModal) {
        openModal();
      } else {
        window.open(ribbonHref, "_blank");
      }
    } else {
      navigate(ribbonHref);
    }
  };

  return (
    <div
      style={isClosed ? { display: "none" } : {}}
      className={cn(
        styles.ribbonContainer,
        isMobile ? "ribbon-mobile" : "ribbon-desktop",
        pathname.replaceAll("/", "-"),
        isPromo ? styles.ribbonPromo + " ribbon-promo" : "ribbon-regular"
      )}
    >
      {ribbonHref ? (
        <LinkHandler
          onClick={(e) => handleRibbonClick(e)}
          path={ribbonHref}
          id="ribbon"
          className={cn(
            styles.ribbonLink,
            "font-dia-normal text-nova-base-black text-center Body-10-Dia-Normal"
          )}
        >
          <span
            dangerouslySetInnerHTML={{ __html: typeof ribbonHtml === "string" ? ribbonHtml : "" }}
          />
        </LinkHandler>
      ) : (
        <section id="ribbon-no-link" className={cn(styles.ribbonLink, styles[className])}>
          <TextElementNova
            element="p"
            text={ribbonHtml}
            className="w-full text-nova-base-black text-center Body-10-Dia-Normal"
          />
        </section>
      )}
      {modalOpen
        ? createPortal(
            <ModalNova hideModal={hideModal} size="large" title={data.modalHeading}>
              <SubscriptionIncludesModal items={data.items} />
            </ModalNova>,
            document.body
          )
        : null}
    </div>
  );
};

export default Ribbon;
